import { useState, useEffect } from 'react';
import { reviewsService } from '../services/reviewsService';

export const useReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [engagements, setEngagements] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Load cached engagements from localStorage on mount
  useEffect(() => {
    const cachedEngagements = localStorage.getItem('userEngagements');
    if (cachedEngagements) {
      try {
        setEngagements(JSON.parse(cachedEngagements));
      } catch (error) {
        console.error('Error parsing cached engagements:', error);
      }
    }
  }, []);

  // Save engagements to localStorage whenever they change
  useEffect(() => {
    if (Object.keys(engagements).length > 0) {
      localStorage.setItem('userEngagements', JSON.stringify(engagements));
    }
  }, [engagements]);

  // Fetch trending reviews
  const fetchTrendingReviews = async () => {
    setIsLoading(true);
    try {
      const reviewsData = await reviewsService.getTrendingReviews(4);
      setReviews(reviewsData);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch trending reviews on mount
  useEffect(() => {
    fetchTrendingReviews();
  }, []);

  const handleShowReviewModal = (review) => {
    setSelectedReview(review);
    setShowReviewModal(true);
  };

  const handleCloseReviewModal = () => {
    setSelectedReview(null);
    setShowReviewModal(false);
  };

  const handleEngagement = async (review, type) => {
    const reviewId = review.review_id;

    // Prevent reporting more than once
    if (type === 'report' && engagements[reviewId]?.reported) {
      return;
    }

    // Update local engagement state (preserve like/dislike on report)
    const updatedEngagements = { ...engagements };
    const prev = updatedEngagements[reviewId] || { like: false, dislike: false, reported: false };
    let next = { ...prev };

    if (type === 'like') {
      next.like = !prev.like;
      if (next.like) {
        next.dislike = false;
      }
      // keep reported as-is
    } else if (type === 'dislike') {
      next.dislike = !prev.dislike;
      if (next.dislike) {
        next.like = false;
      }
      // keep reported as-is
    } else if (type === 'report') {
      // Report is one-time; do not alter like/dislike
      next.reported = true;
    }

    updatedEngagements[reviewId] = next;
    setEngagements(updatedEngagements);

    // Optimistically update like/dislike counts
    setSelectedReview((prevReview) => {
      if (!prevReview || prevReview.review_id !== reviewId) return prevReview;

      let updatedLikes = prevReview.likes;
      let updatedDislikes = prevReview.dislikes;

      if (type === 'like') {
        if (engagements[reviewId]?.like) {
          updatedLikes -= 1;
        } else {
          updatedLikes += 1;
          if (engagements[reviewId]?.dislike) {
            updatedDislikes -= 1;
          }
        }
      } else if (type === 'dislike') {
        if (engagements[reviewId]?.dislike) {
          updatedDislikes -= 1;
        } else {
          updatedDislikes += 1;
          if (engagements[reviewId]?.like) {
            updatedLikes -= 1;
          }
        }
      }

      return {
        ...prevReview,
        likes: updatedLikes,
        dislikes: updatedDislikes,
      };
    });

    // Update via API and use returned counts
    try {
      const isReport = type === 'report';
      const apiResponse = await reviewsService.updateReviewEngagement({
        manager_id: review.manager_id,
        manager_full_name: review.manager_full_name,
        review_id: reviewId,
        like: isReport ? false : updatedEngagements[reviewId].like,
        dislike: isReport ? false : updatedEngagements[reviewId].dislike,
        // Do not send reported=true on like/dislike; only on report action
        reported: isReport ? true : false,
      });

      const serverProfile = apiResponse?.review_profile;

      if (serverProfile) {
        // Update the list item counts
        setReviews((prevReviews) =>
          prevReviews.map((r) =>
            r.review_id === reviewId
              ? { ...r, likes: serverProfile.likes, dislikes: serverProfile.dislikes }
              : r
          )
        );

        // Update the modal's selectedReview counts if open on same review
        setSelectedReview((prev) =>
          prev && prev.review_id === reviewId
            ? { ...prev, likes: serverProfile.likes, dislikes: serverProfile.dislikes }
            : prev
        );
      }
    } catch (error) {
      console.error('Error updating engagement:', error);
      // Optional: rollback changes on error
    }
  };

  // Clear cached engagements (useful for logout)
  const clearEngagements = () => {
    setEngagements({});
    localStorage.removeItem('userEngagements');
  };

  return {
    reviews,
    showReviewModal,
    selectedReview,
    engagements,
    isLoading,
    handleShowReviewModal,
    handleCloseReviewModal,
    handleEngagement,
    fetchTrendingReviews,
    clearEngagements,
  };
}; 